import { useEffect, useState } from "react";
import "../../assets/style/BulkUpload.css";
import { Button, Label } from "reactstrap";
import UploadIcon from "../../assets/img/upload-icon.svg";
import DownloadTemplateIcon from "../../assets/img/download_template.svg";
import CloseIcon from "../../assets/img/cross_file.svg";
import * as XLSX from 'xlsx-js-style';
import { toast } from "react-toastify";
import LinearProgress from '@mui/material/LinearProgress';
import { Box, IconButton, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ErrorFile from '../../assets/img/warning.svg';
import { bulkUploadApi, downloadBulkErrorLogs, getGrievanceDescriptionsApi, getGrievanceTypeMastersApi } from "../../api/grievancesApi";
import { loginSelector } from "../../redux/slices/loginSlice";
import { LoggedUserType } from "../../utils/Types";
import { handleBulkUploadRes } from "../../redux/slices/grievancesSlice";
import { MAX_SIZE_BULK_UPLOAD } from "../../utils/Constant";
import { FormUtils } from "../../utils/FormUtils";
import LabelField from "../../components/LabelField";

type ErrorLogs = {
    fileName: string,
    errorLog: string,
    allowDownload: boolean,
    fileId?: string,
}

const BulkUpload = () => {
    const user: LoggedUserType = useSelector(loginSelector).user;
    const userName = user?.employeeName ? user?.employeeName.split(':')[0] : ""
    const [action, setAction] = useState('upload');
    const [uploadingFiles, setUploadingFiles] = useState<any[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
    const [failedFiles, setFailedFiles] = useState<any[]>([]);
    const [fileProgress, setFileProgress] = useState<any[]>([]);
    const [errorLogs, setErrorLogs] = useState<ErrorLogs[]>([]);
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const fileLimit: number = 15;
    const validExtensions = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    const maxFileSize = MAX_SIZE_BULK_UPLOAD * 1000 * 1000; //10Mb
    const [uploadType, setUploadType] = useState<number>(1);

    const dispatch: any = useDispatch();
    const {
        storeBulkUploadRes,
        storeGrievanceTypeData,
        storeGrievanceDescriptionData
    }= useSelector((state: any) => state.grievances);


    const grievanceTypes = storeGrievanceTypeData?.data ?? [];
    const grievanceDescriptions = storeGrievanceDescriptionData?.data ?? [];    

    const handleFileSelection = (e: any, files: any) => {
        let selectedFiles = Array.from(files);
        if(selectedFiles?.length + uploadedFiles?.length > fileLimit) {
            toast.error(`You can upload a maximum of ${fileLimit} files.`);
            return;
        }
        else {
            const newFiles: any = [];
            const errorFiles: any = [];
            let logs: any = [];
            setFailedFiles([]);
            setErrorLogs([]);
    
            selectedFiles.forEach((file: any) => {
                const fileExtension = file.type;
                const fileSize = file.size;
                if (!validExtensions.includes(fileExtension)) {
                    logs?.push({
                        fileName: file?.name,
                        errorLog: `Invalid extension - ${file.name}`,
                        allowDownload: false
                    });
                    errorFiles.push(file);
                } else if (fileSize > maxFileSize) {
                    logs?.push({
                        fileName: file?.name,
                        errorLog: `${file.name} exceeds the size limit of ${MAX_SIZE_BULK_UPLOAD}MB`,
                        allowDownload: false
                    });
                    errorFiles.push(file);
                } else if (!uploadedFiles.some(f => f.name === file.name)) {
                    newFiles.push(file);
                }
            });
    
            setErrorLogs(logs);
    
            setFailedFiles(prev => {
                const allFiles = [...prev, ...errorFiles];
                const uniqueFiles = Array.from(new Set(allFiles.map(file => file.name)))
                    .map(name => allFiles.find(file => file.name === name));
                return uniqueFiles;
            });
    
            setUploadingFiles(prev => {
                const allFiles = [...prev, ...newFiles];
                const uniqueFiles = Array.from(new Set(allFiles.map(file => file.name)))
                    .map(name => allFiles.find(file => file.name === name));
                setCount(uniqueFiles?.length + errorFiles?.length);
                return uniqueFiles;
            });
    
            setFileProgress(prev => {
                const allProgress = [...prev, ...newFiles.map((file: any) => ({ name: file.name, progress: 0, isUploading: true }))];
                return allProgress;
            });
    
            newFiles.forEach((file: any) => {
                const totalSize = file.size;
                let uploadedSize = 0;
    
                const interval = setInterval(() => {
                    uploadedSize += totalSize * 0.25; // Simulate 25% upload per interval
                    const progressValue = Math.min((uploadedSize / totalSize) * 100, 100);
    
                    setFileProgress(prev => prev.map(fp =>
                        fp.name === file.name ? { ...fp, progress: progressValue, isUploading: progressValue < 100 } : fp
                    ));
    
                    if (progressValue === 100) {
                        clearInterval(interval);
                        setUploadingFiles(prev => prev.filter(f => f.name !== file.name));
                        setUploadedFiles(prev => {
                            const allFiles = [...prev, file];
                            const uniqueFiles = Array.from(new Set(allFiles.map(f => f.name)))
                                .map(name => allFiles.find(f => f.name === name));
                            return uniqueFiles;
                        });
                    }
                }, 500); // Update progress every 500ms
            });
        }
        selectedFiles = [];
        if (e?.target?.value) { e.target.value = ''; }
        if (e?.dataTransfer?.files) { e = undefined; }
    };

    const handleDragOver = (e: any) => {
        e.preventDefault();
    };

    const handleDrop = (e: any) => {
        e.preventDefault();
        handleFileSelection(e, e.dataTransfer.files);
    };

    const removeFile = (file: any) => {
        setUploadingFiles(uploadingFiles.filter(f => f.name !== file.name));
        setUploadedFiles(uploadedFiles.filter(f => f.name !== file.name));
        setFileProgress(fileProgress.filter(fp => fp.name !== file.name));
        setErrorLogs(errorLogs.filter(f => f.fileName !== file.name))
    };

    const handleUpload = () => {
        dispatch(bulkUploadApi(uploadedFiles, userName, uploadType));
    }

    useEffect(() => {
        let logs: any = errorLogs;
        let successFileCount = 0;
        uploadedFiles?.length > 0  && storeBulkUploadRes?.fileSummaries?.forEach((file: any) => {
            if (file?.error) {
                logs.push({
                    fileName: file?.fileName,
                    errorLog: file?.error,
                    allowDownload: false,
                    fileId: file?.fileId
                });
                setFailedFiles(prev => {
                    const failedFile = uploadedFiles.find(f => f.name === file.fileName);
                    return failedFile ? [...prev, failedFile] : prev;
                });
                setUploadedFiles(prev => prev.filter(f => f.name !== file.fileName));
            } else if ((file?.skippedRows && file?.skippedRows > 0) || (file?.duplicateRows && file.duplicateRows > 0)) {
                if(file?.successfulRows > 0){
                    toast.success(`${file?.successfulRows}/${file?.totalRows} records uploaded successfully - ${file?.fileName}`);
                }
                logs.push({
                    fileName: file?.fileName,
                    errorLog: 'File have errors!!',
                    allowDownload: true,
                    fileId: file?.fileId
                });
                setFailedFiles(prev => {
                    const failedFile = uploadedFiles.find(f => f.name === file.fileName);
                    return failedFile ? [...prev, failedFile] : prev;
                });
                setUploadedFiles(prev => prev.filter(f => f.name !== file.fileName));
            } else {
                setUploadedFiles(prev => prev.filter(f => f.name !== file.fileName));
                successFileCount++;
            }
        });
        if(successFileCount){
            toast.success(`${successFileCount} ${successFileCount > 1 ? 'files' : 'file'} uploaded successfully`);
        }
        logs = Array.from(new Set(logs.map((log: ErrorLogs) => log.fileName)))
            .map(name => logs.find((log: ErrorLogs) => log.fileName === name));

        setCount(logs?.length)
        setErrorLogs(logs);
    }, [storeBulkUploadRes]);   

    useEffect(() => {
        setErrorLogs([]);
        setFailedFiles([]);
        dispatch(handleBulkUploadRes({}))
        dispatch(getGrievanceTypeMastersApi())
        dispatch(getGrievanceDescriptionsApi())
    },[])
    

    const downloadErrorLogs = (log: any) => {
        dispatch(downloadBulkErrorLogs(log, userName, uploadType))
    }

    /**
     * Downloads an Excel template file for bulk grievance uploads.
     * 
     * Creates a workbook with two sheets:
     * 1. "Bulk Upload" - Contains empty rows with column headers for grievance data fields
     * 2. "Complaint Mapping" - Contains a reference mapping of complaint descriptions to their types
     * 
     * The template helps users understand:
     * - Required fields and their expected format for bulk upload
     * - Available complaint description IDs and their mappings
     * 
     * @throws {Error} Shows toast notification and logs error if template creation fails
     * @returns {void} Downloads the file directly to the user's device
     */
    const downloadTemplate = (action: string) => {
let wb: XLSX.WorkBook | null = null;
        try {
            // Define column headers for the bulk upload sheet
            // These represent all fields that can be populated in a grievance record
            const headers: any = (action === 'download-general') ? [
                'GrievanceID',
                'ReferenceNumber',
                'LineOfBusiness',
                'TypeOfGrievance',
                'Name',
                'MobileNumber',
                'PolicyNumber',
                'ApplicationNumber',
                'Department',
                'SPOC',
                'Status',
                'ModeOfComplaint',
                'Remarks',
                'FinalDisposition',
                'TypeOfRequest',
                'ComplaintDescriptionId'
            ] : (action === 'download-cpgram') ? [
                'GrievanceID',
                'ReferenceNumber',
                'LineOfBusiness',
                'TypeOfGrievance',
                'Name',
                'MobileNumber',
                'email',
                'PolicyNumber',
                'ApplicationNumber',
                'Status',
                'ModeOfComplaint',
                'Remarks',
                'FinalDisposition',
                'ComplaintDescriptionId',
                'description',
                'state',
                'district',
                'season',
                'year',
                'Complaint Raised Date',
                'Complaint Received to Grievance date',
                'ClaimId'
            ] : [
                'Agent ID',
                'Calling ID',
                'Ticket NCIP Docket No',
                'Support Ticket No',
                'Grievance Intimation Date', 
                'Ticket ReOpen Date',
                'Status',
                'Status Update Time',
                'State',
                'City', 
                'Sub District', 
                'Type Of Request',
                'Type Of Grievance',
                'Crop Lost at',
                'Season',
                'Year',
                'Insurance Company',
                'Application No',
                'Policy No',
                'Caller Contact No',
                'Customer Name',
                'Mobile No',
                'Relation',
                'Relative Name',
                'Policy Premium',
                'Policy Area',
                'Policy Type',
                'Land Survey No',
                'Land Division No',
                'Crop Name',
                'Application Crop Name',
                'Plot State',
                'Plot District',
                'Plot Village',
                'Application Source',
                'Other Sub Category',
                'Crop Stage Type',
                'Loss At',
                'Intimation',
                'Harvest Date',
                'Crop Share',
                'IFSC Code',
                'Farmer Share',
                'Sowing Date',
                'Created By',
                'Description',
                'ComplaintDescriptionId',
                'LineOfBusiness',
                'ModeOfComplaint',
                'Claim ID'
            ];

            const headerRequirement: any = (action === 'download-cpgram') ? [
                {
                    "GrievanceID": "Auto genrated by the GRDS system",
                    "ReferenceNumber": "mandatory",
                    "LineOfBusiness": "mandatory",
                    "TypeOfGrievance": "mandatory",
                    "Name": "mandatory",
                    "MobileNumber": "mandatory",
                    'email': "mandatory",
                    "PolicyNumber": "mandatory",
                    "ApplicationNumber":"Optional",
                    "Status": "mandatory",
                    "ModeOfComplaint": "mandatory",
                    "Remarks": "mandatory",
                    "FinalDisposition": "mandatory",
                    "ComplaintDescriptionId": "mandatory",
                    "description": "mandatory",
                    "state": "mandatory",
                    "district": "mandatory",
                    "season": "optional",
                    "year": "optional",
                    "Complaint Raised Date": "mandatory",
                    "Complaint Received to Grievance date": "mandatory",
                    "ClaimId": "mandatory if type of grievance is claim related"
                }
            ] : (action === 'download-krph') ? [
                {
                'Agent ID': "optional",
                'Calling ID': "optional",
                'Ticket NCIP Docket No': "optional",
                'Support Ticket No': "mandatory",
                'Grievance Intimation Date': "mandatory", 
                'Ticket ReOpen Date': "mandatory",
                'Status': "mandatory",
                'Status Update Time': "mandatory",
                'State': "mandatory",
                'City': "mandatory", 
                'Sub District': "optional", 
                'Type Of Request': "mandatory",
                'Type Of Grievance': "mandatory",
                'Crop Lost at': "mandatory",
                'Season': "mandatory",
                'Year': "mandatory",
                'Insurance Company': "mandatory",
                'Application No': "optional",
                'Policy No': "mandatory",
                'Caller Contact No': "mandatory",
                'Customer Name': "mandatory",
                'Mobile No': "mandatory",
                'Relation': "mandatory",
                'Relative Name': "mandatory",
                'Policy Premium': "mandatory",
                'Policy Area': "mandatory",
                'Policy Type': "mandatory",
                'Land Survey No': "mandatory",
                'Land Division No': "mandatory",
                'Crop Name': "mandatory",
                'Application Crop Name': "mandatory",
                'Plot State': "mandatory",
                'Plot District': "mandatory",
                'Plot Village': "mandatory",
                'Application Source': "mandatory",
                'Other Sub Category': "optional",
                'Crop Stage Type': "optional",
                'Loss At': "optional",
                'Intimation': "optional",
                'Harvest Date': "optional",
                'Crop Share': "mandatory",
                'IFSC Code': "mandatory",
                'Farmer Share': "mandatory",
                'Sowing Date': "mandatory",
                'Created By': "optional",
                'Description': "mandatory",
                'ComplaintDescriptionId': "mandatory",
                'LineOfBusiness': "mandatory",
                'ModeOfComplaint': "mandatory",
                "Claim ID": "mandatory if type of grievance is claim related"
                }
            ] : [];
            
            // Create first worksheet with headers only (empty data rows)
            // Users will fill in the data according to these headers
            const ws1: XLSX.WorkSheet = XLSX.utils.json_to_sheet(headerRequirement, { header: headers });
            
            // Build complaint mapping data by matching grievance descriptions with their types
            // This helps users understand which ComplaintDescriptionId to use
            const complaintMappingData = grievanceDescriptions.map((desc: any) => {
                // Find the corresponding grievance type for this description
                const complaintType = grievanceTypes.find((type: any) => type.id === desc.lobId);
                
                return {
                    'Complaint Description ID': desc.id,
                    'Complaint Description': desc.value,
                    'Complaint Type': complaintType?.value || ''
                };
            });

            // Define headers for the complaint mapping sheet
            const complaintMappingHeaders = [
                'Complaint Description ID',
                'Complaint Description',
                'Complaint Type'
            ];
            
            // Create second worksheet with complaint mapping data
            // This provides a reference guide for valid complaint description IDs
            const ws2: XLSX.WorkSheet = XLSX.utils.json_to_sheet(complaintMappingData, { header: complaintMappingHeaders });

            // Create a new workbook and append both worksheets
            wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws1, "Bulk Upload");
            XLSX.utils.book_append_sheet(wb, ws2, "Complaint Mapping");
        } catch (error) {
            // Handle any errors during template creation
            toast.error('Error creating template. Please try again.');
            console.error('Error in downloadTemplate:', error);
        } finally {
            // Always attempt to write the file if workbook was created successfully
            // This ensures file is downloaded even if there were issues during creation
            if (wb) {
                action === 'download-general' ?
                XLSX.writeFile(wb, 'bulk_upload_grievance_general_template.xlsx') : action === 'download-cpgram' ?
                XLSX.writeFile(wb, 'bulk_upload_grievance_cpgram/ingram_template.xlsx') : 
                XLSX.writeFile(wb, 'bulk_upload_grievance_krph_template.xlsx');
            }
        }    
    }
    
    const handleDownload = (type: any) => {
      setOpen(false);
      setAction(`download-${type}`);
    };

    return (
        <>
            <p className="page-header">Bulk Upload</p>
            <div className='container-bulk'>
                <div className='upload-card'>
                    {action === 'upload' &&
                        <>
                            <div className="header-section">
                                <p className="header">Upload Files</p>
                                <div style={{ position: "relative", display: "inline-block" }}>
                                    <p
                                      className="download-link"
                                      style={{ fontSize: "12px", cursor: "pointer" }}
                                      onClick={() => setOpen(!open)}
                                    >
                                      Download Template ▾
                                    </p>
                            {open && (
                              <div
                                style={{
                                  position: "absolute",
                                  right: 5,
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  background: "#fff",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                  zIndex: 1000,
                                }}
                              >
                                <div
                                  className="dropdown-option"
                                  onClick={() => handleDownload("general")}
                                >
                                  general 
                                </div>
                                <div
                                  className="dropdown-option"
                                  onClick={() => handleDownload("cpgram")}
                                >
                                  cpgram/ingram
                                </div>
                                <div
                                  className="dropdown-option"
                                  onClick={() => handleDownload("krph")}
                                >
                                  krph
                                </div>
                              </div>
                            )}
                          </div>
                            </div>
                            <div>
                            <div>
                                <button
                                    className="upload-section"
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                >
                                    <img src={UploadIcon} alt="Upload" />
                                    <span><b>Drag & drop files or </b>
                                        <label className="download-link">
                                            Browse
                                            <input
                                                type="file"
                                                accept=".xlsx,.xls"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => handleFileSelection(e, e.target.files)}
                                            />
                                        </label>
                                    </span>
                                    <span className="desc">Max file size is {MAX_SIZE_BULK_UPLOAD}MB only. </span>
                                    <span className="desc">Supported formats: .xls, .xlsx</span>
                                </button>
                            </div>
                            {(uploadingFiles.length > 0 || failedFiles?.length > 0) &&
                                <>
                                    <p className="upload-header">Uploading 
                                        {uploadingFiles?.length + failedFiles?.length > 1 &&
                                            <span> {uploadingFiles?.length + failedFiles?.length} / {count} files</span>
                                        }
                                    </p>
                                    {uploadingFiles.map((file, i) => {
                                        const fileProg = fileProgress.find(fp => fp.name === file.name);
                                        return (
                                            <div style={{ marginTop: '2%' }} key={i}>
                                                <Label className="file-btn-progress">
                                                    <span style={{ maxWidth: '80%', overflow: 'hidden', padding: '0 0 4px 5px' }}>{file.name}</span>
                                                    <Box sx={{ color: 'white', width: '100%' }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={fileProg.progress}
                                                            color="inherit"
                                                            sx={{
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: 'rgba(92, 121, 170, 1)',
                                                                },
                                                                backgroundColor: 'white',
                                                                borderRadius: '4px'
                                                            }}
                                                        />
                                                    </Box>
                                                </Label>
                                            </div>
                                        );
                                    })}
                                    {failedFiles.map((file, i) => {
                                        return (
                                            <div style={{ marginTop: '2%' }} key={i}>
                                                <Button className="file-btn-error">
                                                    <span style={{ maxWidth: '80%', overflow: 'hidden', padding: '0 0 0 5px' }}>{file.name}</span>
                                                    <img src={ErrorFile}/>
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </>
                            }
                            {errorLogs?.length > 0 &&
                                <>
                                    <p className="upload-header">Details</p>
                                    {errorLogs?.map((log: any) => {
                                        return (<>
                                            <p className="logs">
                                                <span style={{ width: '15%' }}>File Name: </span>
                                                <span style={{ width: '85%', fontWeight: '600', color: 'rgba(38, 53, 77, 1)' }}>{log?.fileName}</span>
                                            </p>
                                            <p className="logs">
                                                <span style={{ width: '15%' }}>Errors: </span>
                                                <span 
                                                    className={log.allowDownload ? "error-log" : 'error-log-no-download'} 
                                                    style={{ width: '85%' }}
                                                    onClick={() => {log.allowDownload && downloadErrorLogs(log)}}
                                                >
                                                    {log?.errorLog}
                                                </span>
                                            </p>
                                        </>)
                                    })}
                                </>
                            }
                            {uploadedFiles.length > 0 &&
                                <>
                                    <p className="upload-header">Uploaded</p>
                                    {uploadedFiles.map((file, i) => (
                                        <div style={{ marginTop: '2%' }} key={i}>
                                            <Button className="file-btn">
                                                <span style={{ maxWidth: '80%', overflow: 'hidden' }}>{file.name}</span>
                                                <Tooltip 
                                                    title="Remove file" 
                                                    placement="bottom" 
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                backgroundColor: 'black'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <IconButton onClick={() => removeFile(file)}>
                                                        <img src={CloseIcon} />
                                                    </IconButton>
                                                </Tooltip>

                                            </Button>
                                        </div>
                                    ))}
                                </>
                            }
                            </div>
                            <div style={{display: "flex",justifyContent: "space-between", marginTop:"8px", alignItems:"center"}}>
                                <div style={{display: "flex",justifyContent: "space-between", alignItems:"center"}}>
                                <LabelField name='General' className="w-auto" style></LabelField>
                                <input 
                                    type='checkbox'
                                    style={{width:"15px", height:"15px", margin:"0 0 0px 3px"}}
                                    checked={(uploadType == 1)}
                                    onChange={() => {
                                        setUploadType(1)
                                    }
                                    }   
                                />
                                </div>
                                <div style={{display: "flex",justifyContent: "space-between", alignItems:"center"}}>
                                <LabelField name='CPGRAM/INGRAM' className="w-auto"></LabelField>
                                <input 
                                    type='checkbox'
                                    style={{width:"15px", height:"15px", margin:"0 0 0px 3px"}}
                                    checked={(uploadType == 2)}
                                    onChange={() => {
                                        setUploadType(2)
                                    }
                                    }   
                                />
                                </div>
                                <div style={{display: "flex",justifyContent: "space-between", alignItems:"center"}}>
                                <LabelField name='KRPH' className="w-auto"></LabelField>
                                <input 
                                    type='checkbox'
                                    style={{width:"15px", height:"15px", margin:"0 0 0px 3px"}}
                                    checked={(uploadType == 3)}
                                    onChange={() => {
                                        setUploadType(3)
                                    }
                                    }   
                                />
                                </div>
                            </div>
                            <Button
                                className={uploadingFiles?.length > 0 ? "action-btn-progress" : "action-btn"}
                                disabled={!(uploadedFiles?.length > 0)}
                                onClick={handleUpload}
                            >
                                UPLOAD
                            </Button>
                        </>
                    }
                    {(action === 'download-general' || action === 'download-cpgram' || action === 'download-krph') &&
                        <>
                            <div className="header-section">
                                <p className="header">Download Template</p>
                                <p className="download-link" style={{ fontSize: '12px' }} onClick={() => { setAction('upload') }}>Upload Files</p>
                            </div>
                            <p className="desc">Download the template, fill the data and then Upload the file</p>
                            <div className="download-section">
                                <img src={DownloadTemplateIcon} alt="Download Template" />
                                {action === 'download-general' && 
                                  <p style={{ fontSize: '12px', fontWeight: '600' }}>bulk_upload_grievance_general_template.xlsx</p>
                                }
                                {action === 'download-cpgram' && 
                                  <p style={{ fontSize: '12px', fontWeight: '600' }}>bulk_upload_grievance_cpgram/ingram_template.xlsx</p>
                                }
                                {action === 'download-krph' && 
                                  <p style={{ fontSize: '12px', fontWeight: '600' }}>bulk_upload_grievance_krph.xlsx</p>
                                }
                            </div>
                            <Button className="action-btn" onClick={() => downloadTemplate(action)}>
                                DOWNLOAD
                            </Button>
                        </>
                    }
                </div>
            </div>
        </>
    );
};

export default BulkUpload;